import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import RemoveIcon from "@material-ui/icons/Remove";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { listAnnouncements } from "../../services/announcements.api";
import { useAccesoVigente } from "../../context/AccessControl/useAccesoVigente";
import { useTimezone } from "../../hooks/useTimezone";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	filterPendingAnnouncements,
	bulkMarkAnnouncementsReadRemote,
} from "../../helpers/announcementAckStorage";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  announcementCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  priorityChip: {
    fontWeight: 600,
    borderRadius: 16,
  },
  mediaContainer: {
    width: '100%',
    maxHeight: 400,
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    overflow: 'hidden',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  mediaImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
}));

const getMediaPath = (filename) => {
  return `${window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/${filename}`;
};

const getPriorityIcon = (val) => {
  if (val === 1) {
    return <KeyboardArrowUpIcon style={{ color: '#f44336' }} />;
  }
  if (val === 2) {
    return <RemoveIcon style={{ color: '#ff9800' }} />;
  }
  if (val === 3) {
    return <KeyboardArrowDownIcon style={{ color: '#4caf50' }} />;
  }
};

const getPriorityColor = (val) => {
  if (val === 1) {
    return '#f44336';
  }
  if (val === 2) {
    return '#ff9800';
  }
  if (val === 3) {
    return '#4caf50';
  }
};

const getPriorityText = (val) => {
  if (val === 1) {
    return "Alta";
  }
  if (val === 2) {
    return "Media";
  }
  if (val === 3) {
    return "Baja";
  }
};

const AnnouncementsOnLogin = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user, isAuth } = useContext(AuthContext);
  const acceso = useAccesoVigente();
  const { formatDateTime } = useTimezone();

  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isAuth || !user?.companyId || user?.id == null) return;
    // Hasta que los permisos no llegan no se sabe si esta sección existe para
    // este usuario; preguntar antes se paga con un 403 en la cara.
    if (!acceso.loaded) return;

    // Cargar anuncios activos no reconocidos por este usuario (lectura única; nueva versión si admin edita)
    loadActiveAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, user?.companyId, user?.id, acceso.loaded]);

  /** Sin `announcements:ver`, esta sección no existe para el usuario. */
  const loadActiveAnnouncements = async () => {
    if (!acceso.can("announcements:ver")) return;

    try {
      const { data } = await listAnnouncements({ searchParam: "", pageNumber: 1 });
      
      const active = (data.records || []).filter(
        (a) => a.status === true || a.status === 1
      );
      const pending = filterPendingAnnouncements(active);

      if (pending.length > 0) {
        setAnnouncements(pending);
        setCurrentIndex(0);
        setOpen(true);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = async () => {
    if (user?.id != null && announcements.length > 0) {
      try {
        await bulkMarkAnnouncementsReadRemote(announcements.map((a) => a.id));
      } catch (err) {
        toastError(err);
      }
    }
    setOpen(false);
  };

  const handleNext = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (!open || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <span style={{ fontSize: '24px' }} role="img" aria-label="announcement">📢</span>
          <Typography component="span" variant="h5" style={{ fontWeight: 'bold', color: 'white', margin: 0 }}>
            Anuncio {currentIndex + 1} de {announcements.length}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          style={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent style={{ padding: 24 }}>
        <Card className={classes.announcementCard}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h5" style={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                {currentAnnouncement.title}
              </Typography>
              <Box display="flex" gap={1}>
                <Chip
                  size="small"
                  icon={getPriorityIcon(currentAnnouncement.priority)}
                  label={getPriorityText(currentAnnouncement.priority)}
                  className={classes.priorityChip}
                  style={{ 
                    backgroundColor: getPriorityColor(currentAnnouncement.priority) + '20', 
                    color: getPriorityColor(currentAnnouncement.priority) 
                  }}
                />
                <Chip
                  size="small"
                  icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
                  label="Activo"
                  style={{ backgroundColor: '#4caf5020', color: '#4caf50' }}
                />
              </Box>
            </Box>
            
            {currentAnnouncement.mediaPath && (
              <Box className={classes.mediaContainer}>
                <img
                  src={getMediaPath(currentAnnouncement.mediaPath)}
                  alt={currentAnnouncement.mediaName || 'Imagen del anuncio'}
                  className={classes.mediaImage}
                />
              </Box>
            )}
            
            <Typography 
              variant="body1" 
              style={{ 
                color: theme.palette.text.primary,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6
              }}
            >
              {currentAnnouncement.text}
            </Typography>
            
            {currentAnnouncement.mediaName && !currentAnnouncement.mediaPath && (
              <Box display="flex" alignItems="center" mt={2} p={1} style={{ 
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
                borderRadius: 4
              }}>
                <AttachFileIcon style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                  {currentAnnouncement.mediaName}
                </Typography>
              </Box>
            )}
            
            <Box mt={2}>
              <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
                Publicado el {formatDateTime(currentAnnouncement.createdAt)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      
      <DialogActions style={{ 
        padding: '16px 24px',
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        justifyContent: 'space-between'
      }}>
        <Box>
          {currentIndex > 0 && (
            <Button
              onClick={handlePrevious}
              color="primary"
              variant="outlined"
            >
              Anterior
            </Button>
          )}
        </Box>
        <Box display="flex" gap={1}>
          {currentIndex < announcements.length - 1 ? (
            <Button
              onClick={handleNext}
              color="primary"
              variant="contained"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              color="primary"
              variant="contained"
            >
              Cerrar
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementsOnLogin;

